import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { TextField, Button, CircularProgress, Paper, Typography } from '@material-ui/core';
import { MdPerson, MdLocalShipping, MdLocationOn, MdPhone, MdLock, MdPlayArrow, MdCheck } from 'react-icons/md';
import { cargasAPI, truckersAPI, viagensAPI, rastreamentoAPI } from 'services/apiRest';
import Images from 'assets/img';
import * as trackingStorage from 'utils/trackingStorage';
import {
  Container,
  Content,
  Title,
  Subtitle,
  Card,
  SectionTitle,
  InfoCard,
  InfoCardGrid,
  InfoIcon,
  InfoLabel,
  InfoValue,
  CodeInput,
  PinCodeContainer,
  PinCodeInput,
  PinCodeError,
  ButtonContainer,
  LoadingContainer,
  Header,
  HeaderContent,
  Logo,
  StartButton,
  StatusButton,
  InfoCardContent,
} from './styles';

export default function IniciarViagem() {
  const { token } = useParams();
  const history = useHistory();
  const [carga, setCarga] = useState(null);
  const [trucker, setTrucker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState(['', '', '', '']);
  const [codeError, setCodeError] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const pinInputRefs = useRef([]);
  const [locationPermission, setLocationPermission] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [quantidadeEntregas, setQuantidadeEntregas] = useState(0);
  const [encerrarCode, setEncerrarCode] = useState(['', '', '', '']);
  const [encerrarCodeError, setEncerrarCodeError] = useState('');
  const [encerrandoViagem, setEncerrandoViagem] = useState(false);
  const trackingIntervalRef = useRef(null);
  const watchPositionRef = useRef(null);
  const isTrackingActiveRef = useRef(false);
  const encerrarPinInputRefs = useRef([]);
  const serviceWorkerRegistrationRef = useRef(null);

  useEffect(() => {
    registerServiceWorker();
    loadCarga();
  }, [token]);

  // Retomar rastreamento após carga ser carregada
  useEffect(() => {
    if (carga && carga.id) {
      resumeTrackingIfActive();
    }
  }, [carga?.id]);

  // Limpar intervalos ao desmontar
  useEffect(() => {
    return () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
      if (watchPositionRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchPositionRef.current);
        watchPositionRef.current = null;
      }
    };
  }, []);

  const loadCarga = async () => {
    try {
      setLoading(true);
      setError(null);

      // Decodificar token para obter ID da carga
      let cargaId;
      try {
        console.log('🔍 Token recebido (raw):', token);
        
        if (!token || typeof token !== 'string') {
          throw new Error('Token inválido ou vazio');
        }
        
        // Decodificar caracteres especiais da URL (caso o token venha codificado)
        let decodedToken = decodeURIComponent(token);
        console.log('🔍 Token após decodeURIComponent:', decodedToken);
        
        // Se decodeURIComponent não mudou nada, usar o token original
        if (decodedToken === token) {
          decodedToken = token;
        }
        
        // Reverter substituições do base64 URL-safe: - -> +, _ -> /
        let base64Token = decodedToken.replace(/-/g, '+').replace(/_/g, '/');
        console.log('🔍 Token após reverter substituições:', base64Token);
        
        // Adicionar padding se necessário (base64 precisa de múltiplos de 4)
        const paddingNeeded = (4 - (base64Token.length % 4)) % 4;
        if (paddingNeeded > 0) {
          base64Token += '='.repeat(paddingNeeded);
          console.log('🔍 Token com padding:', base64Token);
        }
        
        // Decodificar base64
        let decoded;
        try {
          decoded = atob(base64Token);
          console.log('🔍 Token decodificado:', decoded);
        } catch (atobError) {
          console.error('❌ Erro ao decodificar base64:', atobError);
          throw new Error(`Erro ao decodificar base64: ${atobError.message}`);
        }
        
        // Usar separador especial ||| para extrair o UUID completo
        const parts = decoded.split('|||');
        if (parts.length === 0 || !parts[0]) {
          throw new Error('Token não contém ID da carga');
        }
        
        cargaId = parts[0].trim();
        console.log('🔍 ID da carga extraído:', cargaId);
        
        // Validar formato UUID (formato: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(cargaId)) {
          console.error('❌ ID da carga não é um UUID válido:', cargaId);
          console.error('❌ Token completo decodificado:', decoded);
          setError(`Link inválido. O ID da carga está em formato incorreto: ${cargaId}`);
          setLoading(false);
          return;
        }
        
        console.log('✅ Token decodificado com sucesso. ID da carga:', cargaId);
      } catch (decodeError) {
        console.error('❌ Erro ao decodificar token:', decodeError);
        console.error('❌ Stack:', decodeError.stack);
        setError(`Link inválido: ${decodeError.message || 'Erro ao decodificar token'}. Verifique se o link está correto.`);
        setLoading(false);
        return;
      }

      // Buscar carga usando o token (rota pública, sem autenticação)
      console.log('🔍 Buscando carga com token:', token);
      let cargaData;
      try {
        cargaData = await cargasAPI.buscarPorToken(token);
        console.log('✅ Carga encontrada:', cargaData);
      } catch (apiError) {
        console.error('❌ Erro ao buscar carga na API:', apiError);
        if (apiError.message && (apiError.message.includes('não encontrada') || apiError.message.includes('Token inválido'))) {
          setError('Link inválido ou carga não encontrada. Verifique se o link está correto.');
        } else {
          setError(`Erro ao buscar carga: ${apiError.message || 'Erro desconhecido'}`);
        }
        setLoading(false);
        return;
      }

      if (!cargaData) {
        setError('Carga não encontrada. Verifique se o link está correto.');
        setLoading(false);
        return;
      }

      // Verificar se a carga está encerrada
      if (cargaData.status === 'completed') {
        setError('Esta carga já foi encerrada e não pode mais ser iniciada.');
        setLoading(false);
        return;
      }

      setCarga(cargaData);

      // Usar dados do motorista que vêm na resposta da carga (se disponível)
      if (cargaData.motorista) {
        setTrucker(cargaData.motorista);
      } else if (cargaData.veiculos && cargaData.veiculos[0]?.motorista_cpf) {
        // Fallback: tentar buscar motorista se não vier na resposta
        try {
          const cpfMotorista = cargaData.veiculos[0].motorista_cpf.replace(/\D/g, '');
          if (cpfMotorista && cpfMotorista.length === 11) {
            const truckerData = await truckersAPI.buscarPorCpf(cpfMotorista);
            setTrucker(truckerData);
          }
        } catch (err) {
          console.warn('Erro ao buscar motorista:', err);
        }
      }

      // Usar quantidade de entregas que vem na resposta da carga (se disponível)
      if (cargaData.quantidade_entregas !== undefined) {
        setQuantidadeEntregas(cargaData.quantidade_entregas);
      } else {
        // Fallback: tentar buscar viagens se não vier na resposta
        try {
          const viagens = await viagensAPI.listar({ carga_id: cargaData.id });
          setQuantidadeEntregas(viagens.length || 0);
        } catch (err) {
          console.warn('Erro ao buscar viagens da carga:', err);
          setQuantidadeEntregas(0);
        }
      }

      setLoading(false);
      
      // Verificar se há coordenadas pendentes para enviar
      try {
        const pendingCoords = await trackingStorage.getPendingCoordinates(cargaData.id);
        if (pendingCoords.length > 0) {
          console.log(`📦 ${pendingCoords.length} coordenadas pendentes encontradas, tentando enviar...`);
          // Tentar enviar coordenadas pendentes
          for (const coord of pendingCoords) {
            try {
              await rastreamentoAPI.enviarCoordenadas({
                carga_id: coord.carga_id,
                latitude: coord.latitude,
                longitude: coord.longitude,
                motorista_id: coord.motorista_id,
                motorista_nome: coord.motorista_nome,
                motorista_telefone: coord.motorista_telefone,
              });
              await trackingStorage.removePendingCoordinate(coord.id);
            } catch (err) {
              console.warn('Erro ao enviar coordenada pendente:', err);
            }
          }
        }
      } catch (err) {
        console.warn('Erro ao processar coordenadas pendentes:', err);
      }
    } catch (err) {
      console.error('Erro ao carregar carga:', err);
      setError('Erro ao carregar dados da carga. Verifique se o link está correto.');
      setLoading(false);
    }
  };

  const handlePinChange = (index, value) => {
    // Permitir apenas números
    const numValue = value.replace(/\D/g, '');
    if (numValue.length > 1) return; // Apenas um dígito por campo

    const newCode = [...code];
    newCode[index] = numValue;
    setCode(newCode);
    setCodeError('');

    // Mover para o próximo campo se digitou um número
    if (numValue && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    // Backspace: limpar campo atual e voltar para o anterior se estiver vazio
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
    // Arrow keys: navegar entre campos
    if (e.key === 'ArrowLeft' && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    const newCode = ['', '', '', ''];
    pastedData.split('').forEach((digit, index) => {
      if (index < 4) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    setCodeError('');
    // Focar no último campo preenchido ou no primeiro vazio
    const lastFilledIndex = newCode.findIndex((val, idx) => !val && idx > 0) - 1;
    const focusIndex = lastFilledIndex >= 0 ? lastFilledIndex : (pastedData.length < 4 ? pastedData.length : 3);
    pinInputRefs.current[focusIndex]?.focus();
  };

  const getCodeString = () => {
    return code.join('');
  };

  const validateCode = () => {
    const phone = trucker?.phone || trucker?.telefone;
    if (!trucker || !phone) {
      setCodeError('Telefone do motorista não encontrado.');
      return false;
    }

    const codeString = getCodeString();
    if (codeString.length !== 4) {
      setCodeError('Digite os 4 dígitos completos.');
      return false;
    }

    // Obter últimos 4 dígitos do telefone
    const phoneDigits = phone.replace(/\D/g, '');
    const lastFourDigits = phoneDigits.slice(-4);

    if (codeString === lastFourDigits) {
      setCodeError('');
      return true;
    } else {
      setCodeError('Código inválido. Verifique os últimos 4 dígitos do seu telefone.');
      return false;
    }
  };

  const enviarCoordenadas = async (latitude, longitude) => {
    try {
      if (!carga || !carga.id) {
        console.warn('⚠️ Carga não disponível para enviar coordenadas');
        return;
      }

      const dadosRastreamento = {
        carga_id: carga.id,
        latitude,
        longitude,
        motorista_id: trucker?.id || null,
        motorista_nome: trucker?.name || trucker?.nome || null,
        motorista_telefone: trucker?.phone || null,
      };

      try {
        await rastreamentoAPI.enviarCoordenadas(dadosRastreamento);
        console.log('✅ Coordenadas enviadas com sucesso:', { latitude, longitude });
      } catch (error) {
        // Se falhar, salvar no IndexedDB para envio posterior
        console.warn('⚠️ Erro ao enviar coordenadas, salvando para envio posterior:', error);
        await trackingStorage.savePendingCoordinate(dadosRastreamento);
        
        // Tentar registrar background sync se disponível
        if (serviceWorkerRegistrationRef.current && 'sync' in serviceWorkerRegistrationRef.current) {
          try {
            await serviceWorkerRegistrationRef.current.sync.register('send-tracking-data');
          } catch (syncError) {
            console.warn('⚠️ Background sync não disponível:', syncError);
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro ao processar coordenadas:', error);
      // Salvar como fallback
      const dadosRastreamento = {
        carga_id: carga.id,
        latitude,
        longitude,
        motorista_id: trucker?.id || null,
        motorista_nome: trucker?.name || trucker?.nome || null,
        motorista_telefone: trucker?.phone || null,
      };
      await trackingStorage.savePendingCoordinate(dadosRastreamento);
    }
  };

  // Registrar Service Worker
  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        serviceWorkerRegistrationRef.current = registration;
        console.log('✅ Service Worker registrado:', registration.scope);
      } catch (error) {
        console.warn('⚠️ Erro ao registrar Service Worker:', error);
      }
    }
  };

  // Retomar rastreamento se estava ativo antes
  const resumeTrackingIfActive = async () => {
    try {
      const config = await trackingStorage.getTrackingConfig();
      if (config && config.carga_id && config.active) {
        console.log('🔄 Retomando rastreamento ativo para carga:', config.carga_id);
        
        // Verificar se a carga ainda está ativa (usar token se disponível, senão buscar por ID)
        try {
          let cargaData = null;
          if (token) {
            cargaData = await cargasAPI.buscarPorToken(token);
          }
          
          // Se não conseguiu com token, pode ser que não seja a mesma página, então não retomar
          if (cargaData && cargaData.status !== 'completed' && cargaData.id === config.carga_id) {
            // Mesma carga e ainda ativa
            if (!carga) {
              setCarga(cargaData);
            }
            isTrackingActiveRef.current = true;
            // Aguardar um pouco para garantir que a carga foi carregada
            setTimeout(() => {
              iniciarRastreamento();
              console.log('✅ Rastreamento retomado');
            }, 1000);
          } else if (cargaData && cargaData.status === 'completed') {
            // Carga finalizada, limpar configuração
            console.log('ℹ️ Carga finalizada, parando rastreamento');
            await trackingStorage.clearTrackingConfig();
            pararRastreamento();
          }
        } catch (err) {
          console.warn('⚠️ Não foi possível verificar carga, mas mantendo configuração:', err);
          // Não retomar automaticamente se não conseguir verificar
        }
      }
    } catch (error) {
      console.error('❌ Erro ao retomar rastreamento:', error);
    }
  };

  const iniciarRastreamento = async () => {
    if (watchPositionRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionRef.current);
    }
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
    }

    if (!carga || !carga.id) {
      console.warn('⚠️ Carga não disponível para iniciar rastreamento');
      return;
    }

    isTrackingActiveRef.current = true;

    // Salvar configuração de rastreamento ativo
    await trackingStorage.saveTrackingConfig({
      carga_id: carga.id,
      active: true,
      started_at: Date.now(),
    });

    // Enviar coordenadas imediatamente
    const enviarLocalizacao = () => {
      if (!isTrackingActiveRef.current) {
        return;
      }

      if (!navigator.geolocation) {
        console.error('Geolocalização não suportada');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          await enviarCoordenadas(latitude, longitude);
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000, // Aceitar coordenadas de até 1 minuto
        }
      );
    };

    // Enviar imediatamente
    enviarLocalizacao();

    // Usar watchPosition para monitoramento contínuo
    if (navigator.geolocation && navigator.geolocation.watchPosition) {
      watchPositionRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          if (isTrackingActiveRef.current) {
            const { latitude, longitude } = position.coords;
            await enviarCoordenadas(latitude, longitude);
          }
        },
        (error) => {
          console.error('Erro no watchPosition:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000,
        }
      );
    }

    // Configurar intervalo de 20 minutos como fallback (20 * 60 * 1000 ms)
    trackingIntervalRef.current = setInterval(() => {
      if (isTrackingActiveRef.current) {
        enviarLocalizacao();
      }
    }, 20 * 60 * 1000);

    console.log('📍 Rastreamento iniciado. Monitorando localização continuamente e enviando a cada 20 minutos.');
  };

  const pararRastreamento = async () => {
    isTrackingActiveRef.current = false;
    
    if (watchPositionRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionRef.current);
      watchPositionRef.current = null;
    }
    
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }

    // Limpar configuração de rastreamento
    await trackingStorage.clearTrackingConfig();
    
    console.log('🛑 Rastreamento parado.');
  };

  const requestLocationPermission = async () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não é suportada pelo seu navegador.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Chamar endpoint para iniciar viagem (gravar data_inicio_viagem)
          await cargasAPI.iniciarViagem(carga.id);
          
          setLocationPermission('granted');
          setLocationError('');
          // Iniciar rastreamento contínuo
          iniciarRastreamento();
        } catch (err) {
          console.error('Erro ao iniciar viagem no backend:', err);
          setLocationError('Erro ao iniciar viagem. Tente novamente.');
          setLocationPermission('denied');
        }
      },
      (error) => {
        setLocationPermission('denied');
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Permissão de localização negada. É necessário habilitar para continuar.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Localização indisponível.');
            break;
          case error.TIMEOUT:
            setLocationError('Tempo de espera excedido ao obter localização.');
            break;
          default:
            setLocationError('Erro ao obter localização.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleIniciarViagem = async () => {
    setValidatingCode(true);
    setCodeError('');

    const codeString = getCodeString();
    if (!codeString || codeString.length !== 4) {
      setCodeError('Digite um código de 4 dígitos.');
      setValidatingCode(false);
      return;
    }

    if (!validateCode()) {
      setValidatingCode(false);
      return;
    }

    // Solicitar permissão de localização (que irá chamar o endpoint de iniciar viagem)
    requestLocationPermission();
    setValidatingCode(false);
  };

  const handleEncerrarPinChange = (index, value) => {
    // Permitir apenas números
    const numValue = value.replace(/\D/g, '');
    if (numValue.length > 1) return; // Apenas um dígito por campo

    const newCode = [...encerrarCode];
    newCode[index] = numValue;
    setEncerrarCode(newCode);
    setEncerrarCodeError('');

    // Mover para o próximo campo se digitou um número
    if (numValue && index < 3) {
      encerrarPinInputRefs.current[index + 1]?.focus();
    }
  };

  const handleEncerrarPinKeyDown = (index, e) => {
    // Backspace: limpar campo atual e voltar para o anterior se estiver vazio
    if (e.key === 'Backspace' && !encerrarCode[index] && index > 0) {
      encerrarPinInputRefs.current[index - 1]?.focus();
    }
    // Arrow keys: navegar entre campos
    if (e.key === 'ArrowLeft' && index > 0) {
      encerrarPinInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      encerrarPinInputRefs.current[index + 1]?.focus();
    }
  };

  const handleEncerrarPinPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    const newCode = ['', '', '', ''];
    pastedData.split('').forEach((digit, index) => {
      if (index < 4) {
        newCode[index] = digit;
      }
    });
    setEncerrarCode(newCode);
    setEncerrarCodeError('');
    // Focar no último campo preenchido ou no primeiro vazio
    const lastFilledIndex = newCode.findIndex((val, idx) => !val && idx > 0) - 1;
    const focusIndex = lastFilledIndex >= 0 ? lastFilledIndex : (pastedData.length < 4 ? pastedData.length : 3);
    encerrarPinInputRefs.current[focusIndex]?.focus();
  };

  const getEncerrarCodeString = () => {
    return encerrarCode.join('');
  };

  const handleEncerrarViagem = async () => {
    setEncerrandoViagem(true);
    setEncerrarCodeError('');

    const codeString = getEncerrarCodeString();
    if (!codeString || codeString.length !== 4) {
      setEncerrarCodeError('Digite um código de 4 dígitos.');
      setEncerrandoViagem(false);
      return;
    }

    // Validar código com os primeiros 4 dígitos do ID da carga (sem hífens)
    const cargaIdSemHifens = carga.id.replace(/-/g, '');
    const primeirosQuatroDigitos = cargaIdSemHifens.substring(0, 4).toUpperCase();

    if (codeString.toUpperCase() !== primeirosQuatroDigitos) {
      setEncerrarCodeError('Código inválido. Verifique o código fornecido pelo cliente.');
      setEncerrandoViagem(false);
      return;
    }

    // Parar rastreamento ao encerrar viagem
    pararRastreamento();

    // Chamar endpoint para encerrar viagem (gravar data_fim_viagem e atualizar status)
    try {
      await cargasAPI.encerrarViagem(carga.id);
      
      setEncerrarCodeError('');
      alert('Viagem encerrada com sucesso! O rastreamento foi parado.');
      // Opcional: redirecionar ou atualizar estado
    } catch (err) {
      console.error('Erro ao encerrar viagem:', err);
      setEncerrarCodeError(err.message || 'Erro ao encerrar viagem. Tente novamente.');
    } finally {
      setEncerrandoViagem(false);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} />
        <p>Carregando dados da carga...</p>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <Paper
            style={{
              padding: '20px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '8px',
            }}
          >
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {error}
            </Typography>
          </Paper>
        </Content>
      </Container>
    );
  }

  if (!carga) {
    return (
      <Container>
        <Content>
          <Paper
            style={{
              padding: '20px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '8px',
            }}
          >
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              Carga não encontrada.
            </Typography>
          </Paper>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Logo src={Images.Logo} alt="Logo" />
          <div>
            <Title>ACOMPANHAMENTO DE CARGA</Title>
            <Subtitle>Monitoramento em tempo real</Subtitle>
          </div>
        </HeaderContent>
      </Header>
      <Content>
        <Card>
          <SectionTitle>DADOS DA CARGA</SectionTitle>
          <InfoCardGrid>
            {carga.owner && (
              <InfoCard>
                <InfoIcon>
                  <MdPerson size={24} />
                </InfoIcon>
                <InfoCardContent>
                  <InfoLabel>PROPRIETÁRIO</InfoLabel>
                  <InfoValue>{carga.owner}</InfoValue>
                </InfoCardContent>
              </InfoCard>
            )}
            {carga.type && (
              <InfoCard>
                <InfoIcon>
                  <MdLocalShipping size={24} />
                </InfoIcon>
                <InfoCardContent>
                  <InfoLabel>TIPO</InfoLabel>
                  <InfoValue>{carga.type}</InfoValue>
                </InfoCardContent>
              </InfoCard>
            )}
            {carga.nome_municipio_descarregamento && (
              <InfoCard>
                <InfoIcon>
                  <MdLocationOn size={24} />
                </InfoIcon>
                <InfoCardContent>
                  <InfoLabel>DESTINO</InfoLabel>
                  <InfoValue>
                    {carga.nome_municipio_descarregamento}, {carga.uf_descarregamento}
                  </InfoValue>
                </InfoCardContent>
              </InfoCard>
            )}
            <InfoCard>
              <InfoIcon>
                <MdLocalShipping size={24} />
              </InfoIcon>
              <InfoCardContent>
                <InfoLabel>ENTREGAS</InfoLabel>
                <InfoValue>{quantidadeEntregas}</InfoValue>
              </InfoCardContent>
            </InfoCard>
            {trucker && (
              <>
                <InfoCard>
                  <InfoIcon>
                    <MdPerson size={24} />
                  </InfoIcon>
                  <InfoCardContent>
                    <InfoLabel>MOTORISTA</InfoLabel>
                    <InfoValue>{trucker.name || trucker.nome}</InfoValue>
                  </InfoCardContent>
                </InfoCard>
                <InfoCard>
                  <InfoIcon>
                    <MdPhone size={24} />
                  </InfoIcon>
                  <InfoCardContent>
                    <InfoLabel>TELEFONE</InfoLabel>
                    <InfoValue>{trucker.phone || trucker.telefone || 'N/A'}</InfoValue>
                  </InfoCardContent>
                </InfoCard>
              </>
            )}
          </InfoCardGrid>
        </Card>

        {locationPermission !== 'granted' && (
          <Card>
            <SectionTitle>
              <MdLock size={20} style={{ marginRight: '10px' }} />
              Validação de Segurança
            </SectionTitle>
            <p style={{ marginBottom: '20px', color: '#666', fontSize: '0.95rem', textAlign: 'center' }}>
              Digite os 4 dígitos do seu código de segurança para iniciar a viagem:
            </p>
            <PinCodeContainer onPaste={handlePinPaste}>
              {[0, 1, 2, 3].map((index) => (
                <PinCodeInput
                  key={index}
                  ref={(el) => (pinInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[index]}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  filled={!!code[index]}
                  hasError={!!codeError}
                  autoComplete="off"
                />
              ))}
            </PinCodeContainer>
            {codeError && <PinCodeError>{codeError}</PinCodeError>}
            <ButtonContainer>
              <StartButton
                onClick={handleIniciarViagem}
                disabled={validatingCode || getCodeString().length !== 4}
              >
                {validatingCode ? (
                  <CircularProgress size={24} style={{ color: '#122D58' }} />
                ) : (
                  <>
                    <MdPlayArrow size={24} style={{ marginRight: '8px' }} />
                    INICIAR VIAGEM
                  </>
                )}
              </StartButton>
            </ButtonContainer>
            <StatusButton>
              <MdCheck size={20} style={{ marginRight: '8px' }} />
              Pronto para sair
            </StatusButton>
          </Card>
        )}

        {locationPermission === 'granted' && (
          <>
            <Card>
              <Paper
                style={{
                  padding: '16px',
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  borderRadius: '8px',
                  marginBottom: '20px',
                }}
              >
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  Localização habilitada com sucesso! A viagem foi iniciada.
                </Typography>
              </Paper>
            </Card>

            <Card>
              <SectionTitle>
                <MdLock size={20} style={{ marginRight: '10px' }} />
                Encerrar Viagem
              </SectionTitle>
              <p style={{ marginBottom: '20px', color: '#666', fontSize: '0.95rem', textAlign: 'center' }}>
                Digite o código fornecido pelo cliente na entrega para encerrar a viagem:
              </p>
              <PinCodeContainer onPaste={handleEncerrarPinPaste}>
                {[0, 1, 2, 3].map((index) => (
                  <PinCodeInput
                    key={index}
                    ref={(el) => (encerrarPinInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={encerrarCode[index]}
                    onChange={(e) => handleEncerrarPinChange(index, e.target.value)}
                    onKeyDown={(e) => handleEncerrarPinKeyDown(index, e)}
                    filled={!!encerrarCode[index]}
                    hasError={!!encerrarCodeError}
                    autoComplete="off"
                  />
                ))}
              </PinCodeContainer>
              {encerrarCodeError && <PinCodeError>{encerrarCodeError}</PinCodeError>}
              <ButtonContainer>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleEncerrarViagem}
                  disabled={encerrandoViagem || getEncerrarCodeString().length !== 4}
                  size="large"
                  style={{ marginTop: '20px', padding: '12px 40px' }}
                >
                  {encerrandoViagem ? <CircularProgress size={24} /> : 'Encerrar Viagem'}
                </Button>
              </ButtonContainer>
            </Card>
          </>
        )}

        {locationError && (
          <Paper
            style={{
              padding: '16px',
              backgroundColor: '#fff3e0',
              color: '#e65100',
              borderRadius: '8px',
              marginTop: '20px',
            }}
          >
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {locationError}
            </Typography>
          </Paper>
        )}
      </Content>
    </Container>
  );
}

